import { snippetCompletion } from "@codemirror/autocomplete";
import { StreamLanguage } from "@codemirror/language";
import { Tag } from "@lezer/highlight";

import { getLocaleText } from "shared/boot/i18n";

/*
  Creating our own highlighting tags for our boolean mode
  that can be used by the syntax highlighter in codemirror.
  https://discuss.codemirror.net/t/codemirror-6-stream-syntax-with-custom-tags/2578/13
*/
export const tags = {
  and: Tag.define(),
  or: Tag.define(),
  not: Tag.define(),
  unless: Tag.define(),
  near: Tag.define(),
  first: Tag.define(),
  atleast: Tag.define(),
  field: Tag.define(),
  quotes: Tag.define(),
  brackets: Tag.define(),
  comment: Tag.define(),
  "query-snippet": Tag.define(),
};

const autocompleteSource = (context) => {
  const word = context.matchBefore(/(\w|\/)*/);

  if (word.from === word.to && !context.explicit) {
    return null;
  }

  return {
    from: word.from,
    options: [
      {
        label: "AND",
        detail: getLocaleText("query_editor.mode.and_detail"),
      },
      {
        label: "OR",
        detail: getLocaleText("query_editor.mode.or_detail"),
      },
      {
        label: "NOT",
        detail: getLocaleText("query_editor.mode.not_detail"),
      },
      {
        label: "UNLESS",
        detail: getLocaleText("query_editor.mode.unless_detail"),
      },
      /* eslint-disable no-template-curly-in-string */
      snippetCompletion(
        `/* \${${getLocaleText("query_editor.mode.notes_placeholder")}} */`,
        {
          label: getLocaleText("query_editor.mode.notes_label"),
          detail: getLocaleText("query_editor.mode.notes_detail"),
        }
      ),
      snippetCompletion("NEAR/${n}", {
        label: "NEAR/n",
        detail: getLocaleText("query_editor.mode.near_detail"),
      }),
      snippetCompletion("FIRST/${n}", {
        label: "FIRST/n",
        detail: getLocaleText("query_editor.mode.first_detail"),
      }),
      snippetCompletion("ATLEAST/${n}", {
        label: "ATLEAST/n",
        detail: getLocaleText("query_editor.mode.atleast_detail"),
      }),
    ],
  };
};

const booleanModeStates = {
  start: [
    {
      regex: /\bAND\b/,
      token: "and",
    },
    {
      regex: /\bOR\b/,
      token: "or",
    },
    {
      regex: /\bNOT\b/,
      token: "not",
    },
    {
      regex: /\bUNLESS\b/,
      token: "unless",
    },
    {
      regex: /\bO?NEAR\/[0-9]*\b/,
      token: "near",
    },
    {
      regex: /\bO?FIRST\/[0-9]*\b/,
      token: "first",
    },
    {
      regex: /\bO?ATLEAST\/[0-9]*\b/,
      token: "atleast",
    },
    {
      regex: /[\w]*:/,
      token: "field",
    },
    {
      regex: /(["'])(?:\\(?:\r\n|[\s\S])|(?!\1)[^\\\r\n])*\1/,
      token: "quotes",
    },
    {
      regex: /(\(|\))/,
      token: "brackets",
    },
    {
      regex: /\/\/.*/,
      token: "comment",
    },
    {
      regex: /\/\*/,
      token: "comment",
      next: "comment",
    },
    {
      regex: /{qs-(\d+)(-\S*)?}/,
      token: "query-snippet",
    },
  ],
  comment: [
    { regex: /.*?\*\//, token: "comment", next: "start" },
    { regex: /.*/, token: "comment" },
  ],
  tokenTable: tags,
  languageData: {
    autocomplete: autocompleteSource,
    commentTokens: {
      block: {
        start: "/*",
        end: "*/",
      },
    },
    closeBrackets: {
      brackets: [],
    },
  },
};

/*
  Derived from Codemirror's [simple mode](https://github.com/codemirror/legacy-modes/blob/main/mode/simple-mode.js)
  but supports the tokenTable option to extend the highlighting tags
  to support our custom boolean language.
*/

/* eslint-disable */
function booleanMode(states) {
  ensureState(states, "start");
  let states_ = {},
    meta = states.languageData || {},
    hasIndentation = false;

  for (const state in states) {
    if (state != meta && states.hasOwnProperty(state)) {
      const list = (states_[state] = []),
        orig = states[state];

      for (let i = 0; i < orig.length; i++) {
        const data = orig[i];
        list.push(new Rule(data, states));
        if (data.indent || data.dedent) hasIndentation = true;
      }
    }
  }

  return {
    name: meta.name,
    startState() {
      return {
        state: "start",
        pending: null,
        indent: hasIndentation ? [] : null,
      };
    },
    copyState(state) {
      const s = {
        state: state.state,
        pending: state.pending,
        indent: state.indent && state.indent.slice(0),
      };
      if (state.stack) s.stack = state.stack.slice(0);

      return s;
    },
    token: tokenFunction(states_),
    indent: indentFunction(states_, meta),
    languageData: meta,
    tokenTable: tags,
  };
}

function ensureState(states, name) {
  if (!states.hasOwnProperty(name)) {
    throw new Error(`Undefined state ${name} in simple mode`);
  }
}

function toRegex(val, caret) {
  if (!val) return /(?:)/;
  let flags = "";

  if (val instanceof RegExp) {
    if (val.ignoreCase) flags = "i";
    val = val.source;
  } else {
    val = String(val);
  }

  return new RegExp(`${caret === false ? "" : "^"}(?:${val})`, flags);
}

function asToken(val) {
  if (!val) return null;
  if (val.apply) return val;
  if (typeof val === "string") return val.replace(/\./g, " ");
  const result = [];

  for (let i = 0; i < val.length; i++) {
    result.push(val[i] && val[i].replace(/\./g, " "));
  }

  return result;
}

function Rule(data, states) {
  if (data.next || data.push) ensureState(states, data.next || data.push);
  this.regex = toRegex(data.regex);
  this.token = asToken(data.token);
  this.data = data;
}

function tokenFunction(states) {
  return function (stream, state) {
    if (state.pending) {
      const pend = state.pending.shift();
      if (state.pending.length == 0) state.pending = null;
      stream.pos += pend.text.length;

      return pend.token;
    }

    const curState = states[state.state];

    for (let i = 0; i < curState.length; i++) {
      const rule = curState[i];
      const matches =
        (!rule.data.sol || stream.sol()) && stream.match(rule.regex);

      if (matches) {
        if (rule.data.next) {
          state.state = rule.data.next;
        } else if (rule.data.push) {
          (state.stack || (state.stack = [])).push(state.state);
          state.state = rule.data.push;
        } else if (rule.data.pop && state.stack && state.stack.length) {
          state.state = state.stack.pop();
        }

        if (rule.data.indent)
          state.indent.push(stream.indentation() + stream.indentUnit);
        if (rule.data.dedent) state.indent.pop();
        let { token } = rule;
        if (token && token.apply) token = token(matches);

        if (
          matches.length > 2 &&
          rule.token &&
          typeof rule.token !== "string"
        ) {
          state.pending = [];
          for (let j = 2; j < matches.length; j++)
            if (matches[j])
              state.pending.push({
                text: matches[j],
                token: rule.token[j - 1],
              });
          stream.backUp(
            matches[0].length - (matches[1] ? matches[1].length : 0)
          );

          return token[0];
        }

        if (token && token.join) {
          return token[0];
        }

        return token;
      }
    }

    stream.next();

    return null;
  };
}

function indentFunction(states, meta) {
  return function (state, textAfter) {
    if (
      state.indent == null ||
      (meta.dontIndentStates && meta.doneIndentState.indexOf(state.state) > -1)
    ) {
      return null;
    }

    let pos = state.indent.length - 1,
      rules = states[state.state];

    scan: for (;;) {
      for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];

        if (rule.data.dedent && rule.data.dedentIfLineStart !== false) {
          const m = rule.regex.exec(textAfter);

          if (m && m[0]) {
            pos--;
            if (rule.next || rule.push) rules = states[rule.next || rule.push];
            textAfter = textAfter.slice(m[0].length);
            continue scan;
          }
        }
      }

      break;
    }

    return pos < 0 ? 0 : state.indent[pos];
  };
}

export default StreamLanguage.define(booleanMode(booleanModeStates));
